import { getLocalizedText } from '../engine/localized-text';
import { readSlotValue } from '../engine/slot-values';
import type { SubmissionBindings } from '../engine/submission-bindings';
import type {
  SelectOptions,
  WidgetField,
} from '../model/form-template-document.types';
import { RATING_VALUES, YES_NO_UNKNOWN_VALUES } from '../model/widget-slots';
import type { RatingValue, YesNoUnknownValue } from '../model/widget-slots';
import { formatNumberWithUnit } from './number-value';
import { resolveActionOption } from './resolve-selected-action';

/**
 * The answer a submission holds for one widget, resolved but not yet labelled.
 * Everything locale-independent — which slot carries the answer, which stored
 * values count, how an option resolves to its authored label — is decided here,
 * leaving a host only the two vocabularies it must name in its own
 * translations. Pure and framework-free, so a review surface and a backend
 * record builder read the same submission the same way.
 */
export type AnswerValue =
  | { kind: 'unanswered' }
  | { kind: 'text'; text: string }
  | { kind: 'yesNoUnknown'; value: YesNoUnknownValue }
  | { kind: 'rating'; value: RatingValue }
  | { kind: 'options'; options: SelectedOption[] }
  | DiagnosisAnswer
  | { kind: 'skinType'; id: string }
  | { kind: 'number'; value: number; text: string }
  | ClinicalActionAnswer;

/** A stored diagnosis id whose localized name is resolved by the host. */
export interface DiagnosisAnswer {
  kind: 'diagnosis';
  id: string;
  other?: string;
}

/**
 * A chosen clinical action. `value` is what the submission stores; the rest is
 * projected from the pinned template, so it is absent when the document no
 * longer offers the stored value.
 */
export interface ClinicalActionAnswer {
  kind: 'clinicalAction';
  value: string;
  label?: string;
  groupLabel?: string;
  disposition?: string;
}

/**
 * One selected option, carrying the value the submission stores next to its
 * label: two values can localize to the same label, so only the value
 * identifies a selection.
 */
export interface SelectedOption {
  value: string;
  label: string;
}

const YES_NO_UNKNOWN: ReadonlySet<string> = new Set(YES_NO_UNKNOWN_VALUES);
const RATINGS: ReadonlySet<string> = new Set(RATING_VALUES);

/**
 * Set membership rather than a lookup keyed by the stored value: a stored
 * `toString` would otherwise resolve to an inherited function and be labelled
 * with it.
 */
function isYesNoUnknown(value: string): value is YesNoUnknownValue {
  return YES_NO_UNKNOWN.has(value);
}

function isRating(value: string): value is RatingValue {
  return RATINGS.has(value);
}

/**
 * Localized label for a stored option value. Falls back to the raw value for
 * ref-data options and for a value the template no longer offers, so an answer
 * is never hidden just because its label cannot be resolved.
 */
function optionLabel(
  options: SelectOptions,
  value: string,
  locale: string,
): string {
  if (options.source !== 'inline') {
    return value;
  }
  const item = options.items.find((candidate) => candidate.value === value);
  return item ? getLocalizedText(item.label, locale) : value;
}

/** The answer stored for one widget field, resolved for a locale. */
export function readAnswerValue(
  field: WidgetField,
  bindings: SubmissionBindings,
  locale: string,
): AnswerValue {
  switch (field.widget) {
    case 'yes-no-unknown': {
      const value = readSlotValue(field, bindings, 'value');
      if (typeof value !== 'string') {
        return { kind: 'unanswered' };
      }
      // A value outside the widget's vocabulary reads as plain text, so a
      // drifted answer is still shown rather than dropped.
      return isYesNoUnknown(value)
        ? { kind: 'yesNoUnknown', value }
        : { kind: 'text', text: value };
    }
    case 'rating': {
      const value = readSlotValue(field, bindings, 'selected');
      if (typeof value !== 'string') {
        return { kind: 'unanswered' };
      }
      // Same fail-open reading as yes-no-unknown: a grade outside the widget's
      // vocabulary is still an answer, so it is shown rather than dropped.
      return isRating(value)
        ? { kind: 'rating', value }
        : { kind: 'text', text: value };
    }
    case 'single-select':
    case 'scale': {
      const value = readSlotValue(field, bindings, 'selected');
      if (typeof value !== 'string') {
        return { kind: 'unanswered' };
      }
      return {
        kind: 'options',
        options: [{ value, label: optionLabel(field.options, value, locale) }],
      };
    }
    case 'skin-type': {
      const id = readSlotValue(field, bindings, 'selected');
      return typeof id === 'string'
        ? { kind: 'skinType', id }
        : { kind: 'unanswered' };
    }
    case 'number': {
      const value = readSlotValue(field, bindings, 'value');
      return typeof value === 'number'
        ? {
            kind: 'number',
            value,
            text: formatNumberWithUnit(value, field.params?.unit, locale),
          }
        : { kind: 'unanswered' };
    }
    case 'diagnosis-tree':
    case 'diagnosis-search': {
      const id = readSlotValue(field, bindings, 'selected');
      if (typeof id !== 'string') {
        return { kind: 'unanswered' };
      }
      const other = readSlotValue(field, bindings, 'other');
      return {
        kind: 'diagnosis',
        id,
        ...(typeof other === 'string' && other.length > 0 && { other }),
      };
    }
    case 'multi-select': {
      const raw = readSlotValue(field, bindings, 'selected');
      // A set: the widget offers each option once, so a repeated stored value
      // is corruption, and printing it twice would read as two answers.
      const selected = Array.isArray(raw)
        ? [
            ...new Set(
              raw.filter((value): value is string => typeof value === 'string'),
            ),
          ]
        : [];
      if (selected.length === 0) {
        return { kind: 'unanswered' };
      }
      return {
        kind: 'options',
        options: selected.map((value) => ({
          value,
          label: optionLabel(field.options, value, locale),
        })),
      };
    }
    case 'text':
    case 'note': {
      const value = readSlotValue(field, bindings, 'value');
      return typeof value === 'string' && value.length > 0
        ? { kind: 'text', text: value }
        : { kind: 'unanswered' };
    }
    case 'clinical-action': {
      const value = readSlotValue(field, bindings, 'selected');
      if (typeof value !== 'string') {
        return { kind: 'unanswered' };
      }
      const resolved = resolveActionOption(field, bindings);
      if (!resolved) {
        // A stored value the pinned document no longer offers is still an
        // answer: hand the raw value back unlabelled so the caller fails
        // closed rather than claiming the question was skipped.
        return { kind: 'clinicalAction', value };
      }
      const { option, group } = resolved;
      return {
        kind: 'clinicalAction',
        value,
        label: getLocalizedText(option.label, locale),
        ...(group && { groupLabel: getLocalizedText(group.label, locale) }),
        ...(option.disposition && {
          disposition: getLocalizedText(option.disposition, locale),
        }),
      };
    }
    // Images and locations answer on their own surfaces.
    case 'image-capture':
    case 'location-picker':
      return { kind: 'unanswered' };
  }
}
